'use client';

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { EventLineUpArtist } from './event-course-lineup';

interface EventCourseLineUpTabProps {
    /** Parsed lineup artists resolved from the product's `c_eventLineUp` attribute. */
    lineUp: EventLineUpArtist[];
    /** Eyebrow/section label ("La coppia di docenti" for courses, "Gli chef" for events). */
    sidebarLabel: string;
}

/** Renders a single artist biography as multi-paragraph text, preserving line breaks. */
function ArtistBiography({ biography }: { biography: string }): ReactElement {
    return (
        <div className="body-copy-l flex flex-col gap-4 text-neutral-10">
            {biography.split(/\n{2,}/).map((paragraph) => (
                <p key={paragraph} className="whitespace-pre-line">
                    {paragraph}
                </p>
            ))}
        </div>
    );
}

/** A stable React key for an artist, falling back to the image URL or name when no id is present. */
function artistKey(artist: EventLineUpArtist): string {
    return artist.id ?? artist.imageUrl ?? artist.name ?? '';
}

const LINEUP_AVATAR_OVERLAP_PX = 24;

/** Single circular avatar for a lineup artist. */
function LineUpAvatar({ artist, index }: { artist: EventLineUpArtist; index: number }): ReactElement | null {
    if (!artist.imageUrl) {
        return null;
    }

    const overlapMargin = LINEUP_AVATAR_OVERLAP_PX && index > 0 ? `${-index * LINEUP_AVATAR_OVERLAP_PX}px` : 0;

    return (
        <div
            className="size-28 shrink-0 overflow-hidden rounded-full border-2 border-background bg-sand-90"
            style={{ marginLeft: overlapMargin, zIndex: index }}>
            <img src={artist.imageUrl} alt={artist.name ?? ''} className="size-full object-cover" loading="lazy" />
        </div>
    );
}

/**
 * Circular avatars for the lineup artists.
 * The images are hosted externally (Eventbrite), so they are rendered as plain `<img>` tags
 * and NOT routed through `DynamicImage` (which only rewrites SFCC/DIS-hosted URLs).
 */
function LineUpAvatars({ lineUp, layout }: { lineUp: EventLineUpArtist[]; layout: 'scroll' | 'grid' }): ReactElement {
    if (layout === 'scroll') {
        return (
            <div className="flex overflow-x-auto scrollbar-hide overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {lineUp.map((artist, index) => (
                    <LineUpAvatar key={artistKey(artist)} artist={artist} index={index} />
                ))}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-3 gap-4">
            {lineUp.map((artist, index) => (
                <LineUpAvatar key={artistKey(artist)} artist={artist} index={index} />
            ))}
        </div>
    );
}

/**
 * "Docenti"/"Chef" tab content for event/course PDPs, driven by the product's
 * `c_eventLineUp` attribute (Eventbrite artist extended data).
 *
 * Mobile — the avatars, then an accordion with one collapsible item per artist
 * (name as the trigger, biography as the panel). Desktop — a left sidebar with
 * the eyebrow label and avatars, and a right column listing each artist's name
 * (serif heading) and full biography.
 */
export default function EventCourseLineUpTab({ lineUp, sidebarLabel }: EventCourseLineUpTabProps): ReactElement {
    const { t } = useTranslation('product');

    return (
        <div className="w-full pb-16">
            {/* Mobile: avatars + accordion */}
            <div className="flex flex-col gap-6 lg:hidden">
                <p className="label-m text-neutral-40">{sidebarLabel}</p>
                <LineUpAvatars lineUp={lineUp} layout="scroll" />
                <Accordion type="multiple" className="flex flex-col">
                    {lineUp.map((artist) => (
                        <AccordionItem
                            key={artistKey(artist)}
                            value={artistKey(artist)}
                            className="border-b border-sand-90">
                            <AccordionTrigger className="heading-title-l text-neutral-10">
                                {artist.name ?? t('eventCourse.lineUp.unknownArtist')}
                            </AccordionTrigger>
                            {artist.biography && (
                                <AccordionContent>
                                    <ArtistBiography biography={artist.biography} />
                                </AccordionContent>
                            )}
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>

            {/* Desktop: sidebar (label + avatars) and a biographies column */}
            <div className="hidden lg:grid lg:grid-cols-4 lg:gap-12">
                <aside className="flex flex-col gap-6 lg:col-span-1">
                    <p className="label-m text-neutral-40">{sidebarLabel}</p>
                    <LineUpAvatars lineUp={lineUp} layout="grid" />
                </aside>
                <div className="flex flex-col gap-14 lg:col-span-3">
                    {lineUp.map((artist) => (
                        <div key={artistKey(artist)} className="flex flex-col gap-6">
                            <h2 className="heading-title-l text-neutral-10">
                                {artist.name ?? t('eventCourse.lineUp.unknownArtist')}
                            </h2>
                            {artist.biography && <ArtistBiography biography={artist.biography} />}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
